import defaultAvatar from '@assets/images/avatar/default.png';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { inAppNotificationApiClient as companyInAppNotificationApiClient } from '@src/api/company/inAppNotification/inAppNotificationApiClient';
import { inAppNotificationApiClient as tenantInAppNotificationApiClient } from '@src/api/tenant/inAppNotification/inAppNotificationApiClient';
import { InAppNotification } from '@src/api/types/inAppNotification';
import CloseIcon from '@src/assets/images/common/icon/close.svg';
import { SHARED_URL } from '@src/routes/shared';
import { fetchNotifications as fetchNotificationsCompany } from '@src/store/company/companyInAppNotificationSlice';
import { AppDispatch } from '@src/store/store';
import { fetchNotifications as fetchNotificationsTenant } from '@src/store/tenant/tenantInAppNotificationSlice';
import { useDispatch } from 'react-redux';
import { generatePath, Link } from 'react-router-dom';

interface InAppNotificationTooltipProps {
  notifications?: InAppNotification[];
  openNewTab?: boolean;
  onCancel?: (e?: React.MouseEvent) => void;
  onNotificationClick?: (id: number) => void;
}

const transformReasonText = (notification: InAppNotification) => {
  return `${notification.from_user.display_name}さんが、${notification.channel.name}チャンネルであなたをメンションしました。`;
};

const InAppNotificationTooltip = ({
  notifications,
  openNewTab = false,
  onCancel,
  onNotificationClick,
}: InAppNotificationTooltipProps) => {
  const handleNotificationClick = (notificationId: number) => {
    if (onCancel) {
      onCancel();
    }
    if (onNotificationClick) {
      onNotificationClick(notificationId);
    }
  };

  return (
    <div className='relative h-[346px] w-[329px] rounded bg-white shadow-xl shadow-black/20'>
      {/* Header */}
      <div className='absolute right-[8px] top-[8px] grid size-5 place-items-center rounded-full bg-gray-50 lg:right-3 lg:top-[10px]'>
        <button
          type='button'
          className='flex size-5 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200'
          onClick={onCancel}
          aria-label='閉じる'
        >
          <img
            src={CloseIcon}
            alt=''
            width={16}
            height={16}
          />
        </button>
      </div>
      <div className='flex items-center justify-between px-5 pt-5'>
        <h3 className='text-xl font-semibold'>通知</h3>
      </div>

      {/* Notification List */}
      <div className='relative max-h-[298px] overflow-y-auto overflow-x-hidden'>
        {notifications && notifications.length > 0 ? (
          <div className='ml-5 mr-1 divide-y divide-gray-100'>
            {notifications.map((notification) => (
              <Link
                key={notification.id}
                to={`${generatePath(SHARED_URL.COMMUNITY_CHANNEL, {
                  code: notification.channel.code,
                })}?chatId=${notification.chat_id}${notification.chat.parent_chat_id ? `&force_thread_display=true` : ''}`}
                target={openNewTab ? '_blank' : '_self'}
                onClick={() => handleNotificationClick(notification.id)}
              >
                <div className='flex gap-3 py-3'>
                  {/* Avatar */}
                  <div className='shrink-0'>
                    <img
                      src={notification.from_user.avatar_url || defaultAvatar}
                      alt=''
                      className='size-8 rounded-full object-cover'
                    />
                  </div>

                  {/* Content */}
                  <div className='flex min-w-0 flex-1 flex-col gap-1'>
                    <div className='mr-[10px] text-right text-[10px]'>
                      {notification.created_at}
                    </div>
                    <div className='text-[14px]'>{transformReasonText(notification)}</div>
                    <div className='line-clamp-2 text-[10px]'>{notification.chat.message}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className='px-4 py-8 text-center text-[14px]'>通知はありません。</div>
        )}
      </div>
    </div>
  );
};

const TenantInAppNotificationTooltip = (props: InAppNotificationTooltipProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const handleNotificationClick = async (notificationId: number) => {
    const res = await tenantInAppNotificationApiClient.read(notificationId);
    handleApiResponse(res, () => {
      dispatch(fetchNotificationsTenant());
    });
  };

  return (
    <InAppNotificationTooltip
      {...props}
      onNotificationClick={handleNotificationClick}
    />
  );
};

const CompanyInAppNotificationTooltip = (props: InAppNotificationTooltipProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const handleNotificationClick = async (notificationId: number) => {
    const res = await companyInAppNotificationApiClient.read(notificationId);
    handleApiResponse(res, () => {
      dispatch(fetchNotificationsCompany());
    });
  };

  return (
    <InAppNotificationTooltip
      {...props}
      onNotificationClick={handleNotificationClick}
    />
  );
};

// InAppNotificationTooltipはprivate化
export { CompanyInAppNotificationTooltip, TenantInAppNotificationTooltip };
